// frontend/src/modules/monitoring/pages/MonitoringAnalyticsPage.tsx
//
// Monitoring analytics: activity/idle reports, app usage, productivity
// scoring, and scheduled-vs-actual hours — all owner/manager-only
// (monitoring.view_all on the server). Visual language matches the
// monitoring module + projects ReportsPage (Pearl + Coral tokens).

import * as React from 'react';
import { Info } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { StatCard } from '../../../design-system/components/StatCard';
import { DataTable } from '../../../design-system/components/DataTable';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { NoAccessState } from '../../../design-system/components/NoAccessState';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { HatchedBar } from '../../../design-system/components/HatchedBar';
import { Button } from '../../../components/ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { useApi } from '../../../design-system/useApi';
import { formatDate } from '../../../design-system/format';
import { VISUAL } from '../../../design-system/visual';
import { monitoringAnalyticsPaths } from '../api';
import { DailyActivityBars, type DailyActivityDatum } from '../components/DailyActivityBars';
import type {
  ActivityReport, AppUsageReport, ProductivityReport, ScheduleAdherenceReport,
  AppUsageRow, ProductivityRow, AdherenceEmployeeTotal, AdherenceDayRow,
} from '../types';
import type { EmployeeWithUser } from '../../hr/types';

// ── Date presets ─────────────────────────────────────────────────────────────

type Preset = 'last7' | 'last30' | 'this_month';

function iso(d: Date): string { return d.toISOString().slice(0, 10); }

function rangeFor(preset: Preset): { from: string; to: string } {
  const now = new Date();
  const today = iso(now);
  if (preset === 'last7') {
    return { from: iso(new Date(now.getTime() - 6 * 86_400_000)), to: today };
  }
  if (preset === 'last30') {
    return { from: iso(new Date(now.getTime() - 29 * 86_400_000)), to: today };
  }
  const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  return { from: iso(first), to: today };
}

function hrs(minutes: number): string {
  const m = Math.round(minutes);
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}
const pct = (frac: number) => `${Math.round(frac * 100)}%`;

const ALL = 'all';

// ── Page ─────────────────────────────────────────────────────────────────────

export function MonitoringAnalyticsPage() {
  const [preset, setPreset] = React.useState<Preset>('last7');
  const [employeeId, setEmployeeId] = React.useState<string>(ALL);
  const { from, to } = rangeFor(preset);
  const employeeFilter = employeeId === ALL ? undefined : employeeId;

  const empQ = useApi<{ employees: EmployeeWithUser[] }>('/api/hr/employees');
  const employees = empQ.data?.employees ?? [];

  const activityQ = useApi<ActivityReport>(
    monitoringAnalyticsPaths.activity({ from, to, employeeId: employeeFilter }),
    [preset, employeeId],
  );
  const appsQ = useApi<AppUsageReport>(
    monitoringAnalyticsPaths.apps({ from, to, employeeId: employeeFilter }),
    [preset, employeeId],
  );
  const productivityQ = useApi<ProductivityReport>(
    monitoringAnalyticsPaths.productivity({ from, to }),
    [preset],
  );
  const adherenceQ = useApi<ScheduleAdherenceReport>(
    monitoringAnalyticsPaths.scheduleAdherence({ from, to }),
    [preset],
  );

  const totals = activityQ.data?.totals;

  // Aggregate the per-employee-per-day activity rows into one bar per day
  // (the rows are already SQL-grouped; this just merges employees per date).
  const dailyBars: DailyActivityDatum[] = React.useMemo(() => {
    const byDate = new Map<string, DailyActivityDatum>();
    for (const r of activityQ.data?.rows ?? []) {
      const d = byDate.get(r.date) ?? {
        date: r.date, total_captures: 0, active_captures: 0, idle_captures: 0, activity_ratio: 0,
      };
      d.total_captures += r.total_captures;
      d.active_captures += r.active_captures;
      d.idle_captures += r.idle_captures;
      byDate.set(r.date, d);
    }
    // Fill the full range with zero-days so gaps are visible.
    const out: DailyActivityDatum[] = [];
    for (let t = new Date(`${from}T00:00:00Z`).getTime(); t <= new Date(`${to}T00:00:00Z`).getTime(); t += 86_400_000) {
      const date = iso(new Date(t));
      const d = byDate.get(date) ?? { date, total_captures: 0, active_captures: 0, idle_captures: 0, activity_ratio: 0 };
      d.activity_ratio = d.total_captures > 0 ? d.active_captures / d.total_captures : 0;
      out.push(d);
    }
    return out;
  }, [activityQ.data, from, to]);

  // Adherence endpoint is tenant-wide; respect the employee filter client-side.
  const adherenceTotals: AdherenceEmployeeTotal[] = (adherenceQ.data?.totals ?? [])
    .filter((t) => !employeeFilter || t.employee_id === employeeFilter);
  const adherenceRows: AdherenceDayRow[] = (adherenceQ.data?.rows ?? [])
    .filter((r) => !employeeFilter || r.employee_id === employeeFilter);
  const productivityRows: ProductivityRow[] = (productivityQ.data?.rows ?? [])
    .filter((r) => !employeeFilter || r.employee_id === employeeFilter);

  const appRows: AppUsageRow[] = React.useMemo(() => {
    const rows = appsQ.data?.rows ?? [];
    return appsQ.data?.other ? [...rows, appsQ.data.other] : rows;
  }, [appsQ.data]);

  // The whole page is monitoring.view_all-gated server-side; a 403 on the
  // primary report means everything else will 403 too — say so instead of
  // rendering four "No data" tables.
  if (activityQ.status === 403) {
    return (
      <div>
        <PageHeader
          eyebrow="Work / Monitoring / Analytics"
          title="Activity analytics"
          description="Capture-based activity, app usage, and schedule adherence for your team."
        />
        <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-6xl">
          <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <NoAccessState description="Activity analytics is available to workspace owners and monitoring managers." />
          </section>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="Work / Monitoring / Analytics"
        title="Activity analytics"
        description={`Capture-based activity, app usage, and schedule adherence from ${formatDate(from)} to ${formatDate(to)}. Tracked time is estimated from capture counts.`}
        actions={
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-muted/50 ring-1 ring-border/40">
            <PresetButton active={preset === 'last7'}      onClick={() => setPreset('last7')}>Last 7 days</PresetButton>
            <PresetButton active={preset === 'last30'}     onClick={() => setPreset('last30')}>Last 30 days</PresetButton>
            <PresetButton active={preset === 'this_month'} onClick={() => setPreset('this_month')}>This month</PresetButton>
          </div>
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-6xl space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-muted-foreground">Employee:</span>
          <Select value={employeeId} onValueChange={setEmployeeId}>
            <SelectTrigger className="w-64"><SelectValue placeholder="All employees" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All employees</SelectItem>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {activityQ.data && (
            <span className="text-xs text-muted-foreground ml-auto">
              Capture interval: {Math.round(activityQ.data.capture_interval_seconds / 60)} min
            </span>
          )}
        </div>

        {/* ── Summary cards ── */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Activity ratio"
            value={totals ? pct(totals.activity_ratio) : '—'}
            hint="Active captures ÷ all captures"
          />
          <StatCard
            label="Est. tracked time"
            value={totals ? hrs(totals.estimated_tracked_minutes) : '—'}
            hint="Captures × capture interval (estimate)"
          />
          <StatCard
            label="Captures"
            value={totals ? totals.total_captures.toLocaleString() : '—'}
            hint={totals ? `${totals.days_with_data} day(s) · ${totals.employees} employee(s)` : undefined}
          />
          <StatCard
            label="Idle share"
            value={totals && totals.total_captures > 0 ? pct(totals.idle_captures / totals.total_captures) : '—'}
            hint={totals ? `${totals.idle_captures.toLocaleString()} idle captures` : undefined}
          />
        </div>

        {/* ── Per-day activity ── */}
        <Section title="Daily activity">
          <div className="p-6">
            {activityQ.loading && <LoadingState variant="inline" />}
            {!activityQ.loading && activityQ.error && (
              <ErrorState title="Couldn't load activity" description={activityQ.error} onRetry={activityQ.reload} />
            )}
            {!activityQ.loading && !activityQ.error && (activityQ.data?.rows.length ?? 0) === 0 && (
              <EmptyState title="No captures in range" description="Pick a different range or wait for the agent to upload." />
            )}
            {!activityQ.loading && (activityQ.data?.rows.length ?? 0) > 0 && (
              <DailyActivityBars data={dailyBars} />
            )}
          </div>
        </Section>

        {/* ── App usage ── */}
        <Section title="App usage">
          <DataTable<AppUsageRow>
            loading={appsQ.loading}
            rows={appRows}
            rowKey={(r) => r.app_title}
            columns={[
              {
                key: 'app', header: 'App / window title',
                cell: (r) => (
                  <span className={r.app_title === 'Other' || r.app_title === 'Unknown'
                    ? 'text-muted-foreground'
                    : 'font-medium text-foreground'}>
                    {r.app_title}
                  </span>
                ),
              },
              {
                key: 'share', header: 'Share',
                cell: (r) => (
                  <div className="flex items-center gap-3 min-w-[160px]">
                    <HatchedBar value={r.share} className="w-28" fillClass="bg-coral" label={`${r.app_title} share`} />
                    <span className="font-mono tabular-nums text-xs">{pct(r.share)}</span>
                  </div>
                ),
              },
              { key: 'mins', header: 'Est. time', cell: (r) => <span className="font-mono tabular-nums">{hrs(r.estimated_minutes)}</span> },
              { key: 'captures', header: 'Captures', cell: (r) => r.captures.toLocaleString(), className: 'text-muted-foreground' },
            ]}
            emptyState={
              appsQ.error
                ? <ErrorState title="Couldn't load app usage" description={appsQ.error} onRetry={appsQ.reload} />
                : <EmptyState title="No app data" description="No captures with app titles in this range." />
            }
          />
          {appsQ.data && appsQ.data.distinct_apps > 0 && (
            <p className="px-4 pb-3 text-xs text-muted-foreground">
              {appsQ.data.distinct_apps.toLocaleString()} distinct titles · times are estimates (captures × interval).
            </p>
          )}
        </Section>

        {/* ── Productivity ── */}
        <Section
          title="Productivity score"
          aside={productivityQ.data && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title={productivityQ.data.formula}>
              <Info className="h-3.5 w-3.5" /> How is this calculated?
            </span>
          )}
        >
          <DataTable<ProductivityRow>
            loading={productivityQ.loading}
            rows={productivityRows}
            rowKey={(r) => r.employee_id}
            columns={[
              { key: 'name', header: 'Employee', cell: (r) => <span className="font-medium text-foreground">{r.employee_name}</span> },
              {
                key: 'score', header: 'Score',
                cell: (r) => (
                  <div className="flex items-center gap-3 min-w-[160px]" title={scoreExplanation(r)}>
                    <span className="font-mono tabular-nums font-semibold w-8 text-right">{r.score}</span>
                    <HatchedBar value={r.score / 100} className="w-28" fillClass="bg-coral" label={`${r.employee_name} score`} />
                  </div>
                ),
              },
              { key: 'ratio', header: 'Activity', cell: (r) => <span className="font-mono tabular-nums">{pct(r.activity_ratio)}</span> },
              {
                key: 'coverage', header: 'Coverage',
                cell: (r) => r.coverage == null
                  ? <span className="text-muted-foreground" title="No schedule set — score reflects activity ratio only">no schedule</span>
                  : <span className="font-mono tabular-nums">{pct(r.coverage)}</span>,
              },
              {
                key: 'tracked', header: 'Tracked vs scheduled',
                cell: (r) => (
                  <span className="font-mono tabular-nums text-muted-foreground">
                    {hrs(r.estimated_tracked_minutes)}{r.scheduled_minutes != null ? ` / ${hrs(r.scheduled_minutes)}` : ''}
                  </span>
                ),
              },
            ]}
            emptyState={
              productivityQ.error
                ? <ErrorState title="Couldn't load productivity" description={productivityQ.error} onRetry={productivityQ.reload} />
                : <EmptyState title="No employees" description="No active employees to score in this range." />
            }
          />
        </Section>

        {/* ── Schedule adherence ── */}
        <Section title="Scheduled vs actual hours">
          <DataTable<AdherenceEmployeeTotal>
            loading={adherenceQ.loading}
            rows={adherenceTotals}
            rowKey={(r) => r.employee_id}
            columns={[
              { key: 'name', header: 'Employee', cell: (r) => <span className="font-medium text-foreground">{r.employee_name}</span> },
              {
                key: 'scheduled', header: 'Scheduled',
                cell: (r) => r.has_schedule
                  ? <span className="font-mono tabular-nums">{hrs(r.scheduled_minutes)}</span>
                  : <span className="text-muted-foreground">no schedule</span>,
              },
              { key: 'attended', header: 'Attended', cell: (r) => <span className="font-mono tabular-nums">{hrs(r.attended_minutes)}</span> },
              { key: 'tracked', header: 'Agent-tracked (est.)', cell: (r) => <span className="font-mono tabular-nums">{hrs(r.tracked_minutes)}</span> },
              { key: 'days', header: 'Days present', cell: (r) => r.days_present, className: 'text-muted-foreground' },
              {
                key: 'adherence', header: 'Adherence',
                cell: (r) => r.adherence_pct == null
                  ? <span className="text-muted-foreground">—</span>
                  : (
                    <div className="flex items-center gap-3 min-w-[140px]">
                      <HatchedBar value={Math.min(1, r.adherence_pct / 100)} className="w-24" fillClass="bg-coral" label={`${r.employee_name} adherence`} />
                      <span className="font-mono tabular-nums text-xs">{r.adherence_pct}%</span>
                    </div>
                  ),
              },
            ]}
            emptyState={
              adherenceQ.error
                ? <ErrorState title="Couldn't load schedule adherence" description={adherenceQ.error} onRetry={adherenceQ.reload} />
                : <EmptyState title="No data" description="No attendance or captures in this range." />
            }
          />
          {adherenceQ.data && (
            <p className="px-4 pb-3 text-xs text-muted-foreground">{adherenceQ.data.note}</p>
          )}
        </Section>

        {/* ── Per-day adherence detail ── */}
        <Section title="Daily detail">
          <DataTable<AdherenceDayRow>
            loading={adherenceQ.loading}
            rows={adherenceRows}
            rowKey={(r) => `${r.employee_id}:${r.date}`}
            columns={[
              {
                key: 'date', header: 'Date',
                cell: (r) => (
                  <span className="font-medium text-foreground">
                    {formatDate(r.date)}
                    {r.is_weekend && <span className="ml-2 text-[10px] uppercase tracking-wide text-muted-foreground">weekend</span>}
                  </span>
                ),
              },
              { key: 'name', header: 'Employee', cell: (r) => r.employee_name },
              {
                key: 'scheduled', header: 'Scheduled',
                cell: (r) => r.scheduled_minutes > 0
                  ? <span className="font-mono tabular-nums">{hrs(r.scheduled_minutes)}</span>
                  : <span className="text-muted-foreground">—</span>,
              },
              { key: 'attended', header: 'Attended', cell: (r) => <span className="font-mono tabular-nums">{hrs(r.attended_minutes)}</span> },
              { key: 'tracked', header: 'Tracked (est.)', cell: (r) => <span className="font-mono tabular-nums">{hrs(r.tracked_minutes)}</span> },
              { key: 'sessions', header: 'Sessions', cell: (r) => r.sessions, className: 'text-muted-foreground' },
              {
                key: 'adherence', header: 'Adherence',
                cell: (r) => r.adherence_pct == null
                  ? <span className="text-muted-foreground">—</span>
                  : <span className="font-mono tabular-nums">{r.adherence_pct}%</span>,
              },
            ]}
            emptyState={
              adherenceQ.error
                ? <ErrorState title="Couldn't load schedule adherence" description={adherenceQ.error} onRetry={adherenceQ.reload} />
                : <EmptyState title="No data" description="No attendance or captures in this range." />
            }
          />
        </Section>
      </div>
    </div>
  );
}

// ── Bits ─────────────────────────────────────────────────────────────────────

function scoreExplanation(r: ProductivityRow): string {
  const base = `score = round(100 × activity_ratio × coverage). ` +
    `activity_ratio = ${r.active_captures}/${r.total_captures || 0} active captures = ${pct(r.activity_ratio)}. `;
  if (r.coverage == null) {
    return base + 'No schedule set, so coverage cannot be computed — the score reflects activity ratio alone.';
  }
  return base +
    `coverage = min(1, ${hrs(r.estimated_tracked_minutes)} tracked / ${hrs(r.scheduled_minutes ?? 0)} scheduled) = ${pct(r.coverage)}. ` +
    'Tracked time is an estimate (captures × capture interval).';
}

function Section({ title, aside, children }: { title: string; aside?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h3 className={VISUAL.dashSection}>{title}</h3>
        {aside}
      </div>
      <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
        {children}
      </div>
    </section>
  );
}

function PresetButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <Button size="sm" variant={active ? 'default' : 'ghost'} className="rounded-full" onClick={onClick}>
      {children}
    </Button>
  );
}
